/**
 * Question version-history dialog for the Quiz Editor.
 *
 * Extracted verbatim from QuizEditorPage.jsx during the Phase 3 refactor
 * (no behaviour change).
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { History } from 'lucide-react';
import { useApi } from '../../../hooks/useApi';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../ui/dialog';

function VersionRow({ v, isCurrent }) {
  const ts = v.snapshot_at ? new Date(v.snapshot_at).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '—';
  const chipCls = isCurrent
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : 'bg-muted text-muted-foreground';
  return (
    <div className="rounded-md border bg-card p-3 space-y-1.5" data-testid={`history-row-${v.version_id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant="outline" className={`text-[10px] uppercase ${chipCls}`}>
          {isCurrent ? 'Current' : v.change_type}
        </Badge>
        <span className="text-[11px] text-muted-foreground">{ts}</span>
        {v.language && (
          <Badge variant="outline" className="text-[10px] uppercase">{v.language}</Badge>
        )}
      </div>
      <p className="text-sm break-words">{v.question_text || <em className="text-muted-foreground">(empty)</em>}</p>
      {Array.isArray(v.options) && v.options.length > 0 && (
        <ul className="text-xs space-y-0.5">
          {v.options.map((o, i) => (
            <li key={o.id || `${v.version_id}-opt-${i}`} className={o.is_correct ? 'text-emerald-700' : 'text-muted-foreground'}>
              {o.is_correct ? '✓ ' : '• '}{o.text}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function QuestionHistoryDialog({ open, onClose, quizId, questionId }) {
  const { get } = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await get(`/quizzes/${quizId}/questions/${questionId}/history`);
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || 'Failed to load history');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, quizId, questionId, get]);

  const versions = data?.versions || [];
  const current = data?.current;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-4 h-4 text-[var(--lumyn-content)]" /> Question version history
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Up to 10 prior versions + current, newest first.
          </p>
        </DialogHeader>

        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {loading && <p className="text-sm text-muted-foreground p-4">Loading…</p>}
          {!loading && current && (
            <VersionRow v={current} isCurrent />
          )}
          {!loading && versions.map((v) => (
            <VersionRow key={v.version_id} v={v} />
          ))}
          {!loading && versions.length === 0 && current && (
            <p className="text-xs text-muted-foreground p-3 italic">
              No history yet — this question hasn't been edited, regenerated, or translated.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="history-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
